import React, { useState, useEffect } from "react"
import { Col, Row, Spinner, UncontrolledAlert, Table } from "reactstrap"
import { Link } from "react-router-dom"
import api from "API/Api"
import { usePermissions } from "API/PermissionContext"

const PaymentConfig = () => {
  const { canEdit, MODULE } = usePermissions()
  const MID = MODULE.PAYMENTS

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [tab, setTab] = useState("general")

  // Config fields
  const [config, setConfig] = useState({
    billing_cycle: "monthly", custom_cycle_days: [], payment_day_offset: 2,
    auto_producer_payments: false, auto_vendor_payments: false, auto_transporter_payments: false,
    auto_generate_bills: false, auto_calculate_deductions: true, auto_send_sms_on_payment: true,
    approval_mode: "single", min_payout_amount: "1", max_auto_payout: "100000",
    upi_enabled: false, upi_provider: "", upi_api_key: "", upi_api_secret: "", upi_merchant_id: "",
    bank_file_enabled: true, bank_file_format: "neft_csv",
    bank_name: "", company_account_no: "", company_ifsc: "",
    hold_payment_days: 0, is_active: false,
  })

  const [cycles, setCycles] = useState({})
  const [upiProviders, setUpiProviders] = useState({})
  const [customDays, setCustomDays] = useState("")

  // Route overrides
  const [overrides, setOverrides] = useState([])
  const [routes, setRoutes] = useState([])
  const [newOverride, setNewOverride] = useState({ route_id: "", override_cycle: "monthly", is_active: true })

  useEffect(() => { loadConfig() }, [])

  const loadConfig = async () => {
    try {
      const [cfgResp, overridesResp, routesResp] = await Promise.all([
        api.get("payment-config"),
        api.get("payment-config/overrides"),
        api.get("routes?per_page=500"),
      ])

      if (cfgResp.data.success) {
        if (cfgResp.data.data) {
          const d = cfgResp.data.data
          setConfig(prev => ({ ...prev, ...d }))
          setCustomDays(Array.isArray(d.custom_cycle_days) ? d.custom_cycle_days.join(", ") : "")
        }
        setCycles(cfgResp.data.billing_cycles || {})
        setUpiProviders(cfgResp.data.upi_providers || {})
      }
      if (overridesResp.data.success) setOverrides(overridesResp.data.data || [])
      const rd = routesResp.data?.data
      setRoutes(Array.isArray(rd) ? rd : rd?.data || [])
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  const updateConfig = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }))
  }

  const saveConfig = async () => {
    setSaving(true); setSaved(false)
    try {
      const data = { ...config }
      if (data.billing_cycle === "custom") {
        data.custom_cycle_days = customDays.split(",").map(d => parseInt(d.trim())).filter(d => !isNaN(d) && d >= 1 && d <= 31)
      }
      // Don't send masked keys
      delete data.upi_api_key_masked; delete data.upi_api_secret_masked
      delete data.upi_api_key_set; delete data.upi_api_secret_set

      await api.post("payment-config", data)
      setSaved(true); setTimeout(() => setSaved(false), 3000)
    } catch (e) { console.error(e) }
    setSaving(false)
  }

  const saveOverride = async () => {
    if (!newOverride.route_id) return
    try {
      await api.post("payment-config/overrides", newOverride)
      setNewOverride({ route_id: "", override_cycle: "monthly", is_active: true })
      const resp = await api.get("payment-config/overrides")
      if (resp.data.success) setOverrides(resp.data.data || [])
    } catch (e) { console.error(e) }
  }

  const deleteOverride = async (id) => {
    try {
      await api.delete("payment-config/overrides/" + id)
      setOverrides(prev => prev.filter(o => o.id !== id))
    } catch (e) { console.error(e) }
  }

  const tabStyle = (t) => ({
    padding: "10px 20px", cursor: "pointer", fontWeight: tab === t ? "600" : "400",
    color: tab === t ? "#6366f1" : "#64748b", borderBottom: tab === t ? "3px solid #6366f1" : "3px solid transparent",
    fontSize: "14px", transition: "all 0.2s",
  })

  const sectionStyle = { background: "#fff", borderRadius: "12px", padding: "20px", marginBottom: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }
  const labelStyle = { fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "4px" }
  const helpStyle = { fontSize: "11px", color: "#94a3b8", marginTop: "2px" }

  if (loading) return <div style={{textAlign:"center",padding:"60px"}}><Spinner color="primary" /></div>

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}><h4><i className="fas fa-cog me-2" style={{color:"#6366f1"}}></i>Payment Configuration</h4></Col>
          <Col md={6} style={{textAlign:"right"}}><Link className="dashBtn" to="/payment-dashboard"><i className="fas fa-chevron-left"></i> <b>Dashboard</b></Link></Col>
        </Row>
      </div>
      <div className="dashContent">

        {/* Status Toggle */}
        <div style={{...sectionStyle, display:"flex", justifyContent:"space-between", alignItems:"center", background: config.is_active ? "#f0fdf4" : "#fef2f2", border: config.is_active ? "1px solid #bbf7d0" : "1px solid #fecaca"}}>
          <div>
            <div style={{fontWeight:"700",color: config.is_active ? "#166534" : "#991b1b"}}><i className={`fas ${config.is_active?"fa-check-circle":"fa-times-circle"} me-2`}></i>Payment System: {config.is_active ? "ACTIVE" : "DISABLED"}</div>
            <div style={{fontSize:"12px",color:"#6c757d",marginTop:"2px"}}>Toggle to enable/disable the automatic payment system</div>
          </div>
          <div className="form-check form-switch">
            <input className="form-check-input" type="checkbox" style={{width:"48px",height:"24px"}} checked={config.is_active} onChange={e=>updateConfig("is_active", e.target.checked)} />
          </div>
        </div>

        {saved && <UncontrolledAlert color="success"><i className="fas fa-check-circle me-1"></i><strong>Configuration saved successfully!</strong></UncontrolledAlert>}

        {/* Tabs */}
        <div style={{display:"flex",borderBottom:"1px solid #e2e8f0",marginBottom:"20px"}}>
          <div style={tabStyle("general")} onClick={()=>setTab("general")}><i className="fas fa-sliders-h me-1"></i>General</div>
          <div style={tabStyle("bank")} onClick={()=>setTab("bank")}><i className="fas fa-university me-1"></i>Bank & UPI</div>
          <div style={tabStyle("routes")} onClick={()=>setTab("routes")}><i className="fas fa-route me-1"></i>Route Overrides</div>
        </div>

        {/* ══════ TAB 1: General ══════ */}
        {tab === "general" && (
          <>
            {/* Billing Cycle */}
            <div style={sectionStyle}>
              <h6 style={{fontWeight:"600",color:"#334155",marginBottom:"16px"}}><i className="fas fa-calendar-alt me-2"></i>Billing Cycle</h6>
              <Row>
                <Col md={4}>
                  <label style={labelStyle}>Payment Frequency</label>
                  <select className="form-control" value={config.billing_cycle} onChange={e=>updateConfig("billing_cycle", e.target.value)}>
                    {Object.entries(cycles).map(([k,v]) => <option key={k} value={k}>{v.label} — {v.description}</option>)}
                  </select>
                </Col>
                {config.billing_cycle === "custom" && (
                  <Col md={4}>
                    <label style={labelStyle}>Custom Payment Days</label>
                    <input type="text" className="form-control" placeholder="e.g. 5, 20" value={customDays} onChange={e=>setCustomDays(e.target.value)} />
                    <div style={helpStyle}>Comma-separated dates of the month</div>
                  </Col>
                )}
                <Col md={4}>
                  <label style={labelStyle}>Payment Day Offset</label>
                  <input type="number" className="form-control" min="0" max="10" value={config.payment_day_offset} onChange={e=>updateConfig("payment_day_offset", parseInt(e.target.value)||0)} />
                  <div style={helpStyle}>Days after period ends before processing</div>
                </Col>
              </Row>
            </div>

            {/* Auto Toggles */}
            <div style={sectionStyle}>
              <h6 style={{fontWeight:"600",color:"#334155",marginBottom:"16px"}}><i className="fas fa-robot me-2"></i>Automation Toggles</h6>
              <Row>
                {[
                  {field:"auto_producer_payments",label:"Auto Producer Payments",help:"Automatically process payments for producers"},
                  {field:"auto_vendor_payments",label:"Auto Vendor Payments",help:"Automatically process payments for vendors"},
                  {field:"auto_generate_bills",label:"Auto Generate Bills",help:"Auto-create route bills when batch is generated"},
                  {field:"auto_calculate_deductions",label:"Auto Calculate Deductions",help:"Auto-deduct loans, advances, recoveries"},
                  {field:"auto_send_sms_on_payment",label:"Send SMS on Payment",help:"Auto-notify producers/vendors when paid"},
                ].map((t,i) => (
                  <Col md={6} key={i} className="mb-3">
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",background:"#f8fafc",borderRadius:"8px"}}>
                      <div>
                        <div style={{fontWeight:"600",fontSize:"13px"}}>{t.label}</div>
                        <div style={helpStyle}>{t.help}</div>
                      </div>
                      <div className="form-check form-switch">
                        <input className="form-check-input" type="checkbox" style={{width:"40px",height:"20px"}} checked={config[t.field]} onChange={e=>updateConfig(t.field, e.target.checked)} />
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>

            {/* Approval & Limits */}
            <div style={sectionStyle}>
              <h6 style={{fontWeight:"600",color:"#334155",marginBottom:"16px"}}><i className="fas fa-shield-alt me-2"></i>Approval & Limits</h6>
              <Row>
                <Col md={4}>
                  <label style={labelStyle}>Approval Mode</label>
                  <select className="form-control" value={config.approval_mode} onChange={e=>updateConfig("approval_mode", e.target.value)}>
                    <option value="none">None — Auto-approve all</option>
                    <option value="single">Single Approver</option>
                    <option value="multi">Multi-level Approval</option>
                  </select>
                </Col>
                <Col md={4}>
                  <label style={labelStyle}>Minimum Payout Amount (₹)</label>
                  <input type="number" className="form-control" min="0" step="0.01" value={config.min_payout_amount} onChange={e=>updateConfig("min_payout_amount", e.target.value)} />
                  <div style={helpStyle}>Skip payments below this amount</div>
                </Col>
                <Col md={4}>
                  <label style={labelStyle}>Max Auto Payout (₹)</label>
                  <input type="number" className="form-control" min="0" value={config.max_auto_payout} onChange={e=>updateConfig("max_auto_payout", e.target.value)} />
                  <div style={helpStyle}>Amounts above this require manual approval</div>
                </Col>
              </Row>
            </div>
          </>
        )}

        {/* ══════ TAB 2: Bank & UPI ══════ */}
        {tab === "bank" && (
          <>
            {/* Bank File */}
            <div style={sectionStyle}>
              <h6 style={{fontWeight:"600",color:"#334155",marginBottom:"16px"}}><i className="fas fa-university me-2"></i>Bank File (CSV Auto-Generation)</h6>
              <Row>
                <Col md={3}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"12px"}}>
                    <label style={labelStyle}>Enable Bank File</label>
                    <div className="form-check form-switch"><input className="form-check-input" type="checkbox" checked={config.bank_file_enabled} onChange={e=>updateConfig("bank_file_enabled", e.target.checked)} /></div>
                  </div>
                </Col>
                <Col md={3}>
                  <label style={labelStyle}>File Format</label>
                  <select className="form-control" value={config.bank_file_format} onChange={e=>updateConfig("bank_file_format", e.target.value)}>
                    <option value="neft_csv">NEFT/RTGS CSV</option>
                    <option value="nach_csv">NACH CSV</option>
                    <option value="imps_csv">IMPS CSV</option>
                    <option value="custom">Custom Format</option>
                  </select>
                </Col>
                <Col md={3}>
                  <label style={labelStyle}>Company Bank Name</label>
                  <input type="text" className="form-control" value={config.bank_name||""} onChange={e=>updateConfig("bank_name", e.target.value)} placeholder="e.g. State Bank of India" />
                </Col>
              </Row>
              <Row className="mt-2">
                <Col md={4}>
                  <label style={labelStyle}>Company Account Number</label>
                  <input type="text" className="form-control" value={config.company_account_no||""} onChange={e=>updateConfig("company_account_no", e.target.value)} placeholder="e.g. 1234567890" />
                </Col>
                <Col md={4}>
                  <label style={labelStyle}>Company IFSC Code</label>
                  <input type="text" className="form-control" value={config.company_ifsc||""} onChange={e=>updateConfig("company_ifsc", e.target.value)} placeholder="e.g. SBIN0001234" />
                </Col>
              </Row>
              <div style={{...helpStyle, marginTop:"12px"}}><i className="fas fa-info-circle me-1"></i>CSV file is auto-generated by the system when you process a payment batch. You just download it and upload to your bank portal.</div>
            </div>

            {/* UPI Config */}
            <div style={sectionStyle}>
              <h6 style={{fontWeight:"600",color:"#334155",marginBottom:"16px"}}><i className="fas fa-mobile-alt me-2"></i>UPI Payout Configuration</h6>
              <Row className="mb-3">
                <Col md={3}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <label style={labelStyle}>Enable UPI Payouts</label>
                    <div className="form-check form-switch"><input className="form-check-input" type="checkbox" checked={config.upi_enabled} onChange={e=>updateConfig("upi_enabled", e.target.checked)} /></div>
                  </div>
                </Col>
                {config.upi_enabled && (
                  <>
                    <Col md={3}>
                      <label style={labelStyle}>UPI Provider</label>
                      <select className="form-control" value={config.upi_provider||""} onChange={e=>updateConfig("upi_provider", e.target.value)}>
                        <option value="">Select Provider</option>
                        {Object.entries(upiProviders).map(([k,v]) => <option key={k} value={k}>{v.name}</option>)}
                      </select>
                    </Col>
                    <Col md={3}>
                      <label style={labelStyle}>API Key</label>
                      <input type="password" className="form-control" placeholder={config.upi_api_key_set ? "••••••••" : "Enter API Key"} onChange={e=>updateConfig("upi_api_key", e.target.value)} />
                    </Col>
                    <Col md={3}>
                      <label style={labelStyle}>API Secret</label>
                      <input type="password" className="form-control" placeholder={config.upi_api_secret_set ? "••••••••" : "Enter API Secret"} onChange={e=>updateConfig("upi_api_secret", e.target.value)} />
                    </Col>
                    <Col md={3} className="mt-2">
                      <label style={labelStyle}>Merchant ID</label>
                      <input type="text" className="form-control" value={config.upi_merchant_id||""} onChange={e=>updateConfig("upi_merchant_id", e.target.value)} placeholder="Merchant/Account ID" />
                    </Col>
                  </>
                )}
              </Row>
            </div>
          </>
        )}

        {/* ══════ TAB 3: Route Overrides ══════ */}
        {tab === "routes" && (
          <div style={sectionStyle}>
            <h6 style={{fontWeight:"600",color:"#334155",marginBottom:"16px"}}><i className="fas fa-route me-2"></i>Route Payment Schedule Overrides</h6>
            <p style={{fontSize:"12px",color:"#64748b",marginBottom:"16px"}}>Override the company-wide billing cycle for specific routes. Routes without an override will use the company default (<strong>{config.billing_cycle}</strong>).</p>

            {/* Add Override */}
            <Row className="mb-3" style={{background:"#f0f9ff",padding:"12px",borderRadius:"8px",alignItems:"flex-end"}}>
              <Col md={4}>
                <label style={labelStyle}>Route</label>
                <select className="form-control form-control-sm" value={newOverride.route_id} onChange={e=>setNewOverride(p=>({...p, route_id: e.target.value}))}>
                  <option value="">Select Route</option>
                  {routes.filter(r => !overrides.find(o => String(o.route_id) === String(r.id))).map((r,i)=><option key={i} value={r.id}>{r.routeName}</option>)}
                </select>
              </Col>
              <Col md={3}>
                <label style={labelStyle}>Override Cycle</label>
                <select className="form-control form-control-sm" value={newOverride.override_cycle} onChange={e=>setNewOverride(p=>({...p, override_cycle: e.target.value}))}>
                  {Object.entries(cycles).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </Col>
              <Col md={2}><button className="btn btn-primary btn-sm" onClick={saveOverride} disabled={!newOverride.route_id}><i className="fas fa-plus me-1"></i>Add</button></Col>
            </Row>

            {/* Overrides Table */}
            {overrides.length === 0 ? (
              <div style={{textAlign:"center",padding:"20px",color:"#94a3b8"}}>No route overrides configured. All routes use company default.</div>
            ) : (
              <Table className="table table-sm" style={{fontSize:"12px"}}>
                <thead><tr style={{background:"#f8fafc",fontSize:"10px",textTransform:"uppercase",color:"#64748b"}}><th>Route</th><th>Override Cycle</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {overrides.map((o,i) => (
                    <tr key={o.id||i}>
                      <td style={{fontWeight:"600"}}>{o.routeName || `Route #${o.route_id}`}</td>
                      <td><span className="badge bg-info">{o.override_cycle}</span></td>
                      <td>{o.is_active ? <span className="badge bg-success">Active</span> : <span className="badge bg-secondary">Inactive</span>}</td>
                      <td><button className="btn btn-outline-danger btn-sm" style={{fontSize:"10px"}} onClick={()=>deleteOverride(o.id)}><i className="fas fa-trash"></i></button></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        )}

        {/* Save Button */}
        <div style={{textAlign:"right",marginTop:"20px"}}>
          <button className="btn btn-primary px-4" onClick={saveConfig} disabled={saving}>
            {saving ? <><Spinner size="sm" className="me-1" />Saving...</> : <><i className="fas fa-save me-1"></i>Save Configuration</>}
          </button>
        </div>
      </div>
    </div>
  )
}

export default PaymentConfig
